import * as React from 'react';
import { type PropType, type Selector, type OptionType, type ProductPage, SelectType } from './ProductVariantsSelectorProvider';
import { isPage, type PageOrError, replaceState, URLX } from '@avensia/excite';

import type { JSX } from "react";

function compareObjects(objectA: any, objectB: any, excludePropName?: string) {
  if (!objectA || !objectB) return false;
  const aProperties = Object.getOwnPropertyNames(objectA);
  const bProperties = Object.getOwnPropertyNames(objectB);
  if (aProperties.length !== bProperties.length) {
    return false;
  }

  for (let i = 0; i < aProperties.length; i++) {
    const propName = aProperties[i];
    if (objectA[propName] !== objectB[propName] && excludePropName !== propName) {
      return false;
    }
  }

  return true;
}

export function useVariantVariesOn(
  props: React.PropsWithChildren<PropType>,
  addOrUpdate: (selector: Selector) => void,
  isSmallViewport: boolean,
  variantTransform: (option: OptionType, isSelectedValue: boolean, placeholder?: string | undefined) => JSX.Element,
) {
  const { variants, variesOn } = props;

  const extendPartialCache = React.useCallback(
    (page: PageOrError) => {
      if (isPage(page)) {
        (page as ProductPage).breadcrumbs = props.breadcrumbs;
        (page as ProductPage).variants = props.variants;
        (page as ProductPage).description = props.description;
        (page as ProductPage).variesOn = props.variesOn;
        return page;
      }
    },
    [props.breadcrumbs, props.description, props.variants, props.variesOn],
  );

  const optionTransform = React.useCallback(
    (option: NordicNest.Api.SelectOptionApiModel) => variantTransform(option, false),
    [variantTransform, variants],
  );
  if (variesOn) {
    for (const [key, label] of Object.entries(variesOn)) {
      const options: OptionType[] = [];

      props.variants.forEach(v => {
        if (compareObjects(v.variantDefinition, props.currentVariant?.variantDefinition, key)) {
          options.push({
            text: v.variantDefinition?.[key] as string,
            value: v.variantDefinition?.[key] as string,
            image: v.image,
            variant: v,
          });
        }
      });

      const onChangeOption = (option: { [k: string]: string }) => {
        const variantDefinition = { ...props.currentVariant?.variantDefinition, ...option };
        const selectedVariant = variants
          .filter(v => v.variantDefinition)
          .find(v => compareObjects(v.variantDefinition, variantDefinition));

        if (selectedVariant) {
          isSmallViewport && scrollTo(0, 0);
          const url = new URLX(selectedVariant.url);
          replaceState(url, { extendPartialCache });
        }
      };

      const value = props.currentVariant?.variantDefinition[key] as string;

      const selectedOption = options?.find(o => o.value === value);

      const selectedTransform = (option: NordicNest.Api.SelectOptionApiModel) => variantTransform(option, true, label);

      addOrUpdate({
        id: key,
        value: value,
        selectedOption,
        options,
        onChangeOption: v => onChangeOption({ [key]: v }),
        selectedTransform,
        optionTransform,
        showVariantSelect: true,
        type: SelectType.VariesOn,
        additionalPrice: { inclVat: 0, exclVat: 0, vatPercent: 0 },
        placeholder: label,
      });
    }
  }
}
